import React, { useState, useEffect } from "react";
import {
    makeStyles,
    Paper,
    Grid,
    TextField,
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    IconButton,
    FormControl,
    InputLabel,
    MenuItem,
    Select
} from "@material-ui/core";
import { Formik, Form, Field } from 'formik';
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";
import useTableStyles from "../TableStyles";

import { Edit as EditIcon } from "@material-ui/icons";

import { toast } from "sonner";
import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";


const useStyles = makeStyles(theme => ({
    root: {
        width: '100%'
    },
    mainPaper: {
        width: '100%',
        flex: 1,
        padding: theme.spacing(2, 3)
    },
    fullWidth: {
        width: '100%'
    },
    textfield: {
        width: '100%',
        "& .MuiOutlinedInput-root": {
            borderRadius: 10,
            fontFamily: '"Inter", "Poppins", sans-serif',
            "& fieldset": {
                borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.12)" : "#E2E8F0",
            },
            "&:hover fieldset": {
                borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.2)" : "#CBD5E1",
            },
            "&.Mui-focused fieldset": {
                borderColor: "#0B3C84",
            },
        },
        "& .MuiInputLabel-outlined": {
            fontFamily: '"Inter", "Poppins", sans-serif',
            "&.Mui-focused": {
                color: "#0B3C84",
            },
        },
    },
    selectContainer: {
        width: "100%",
        "& .MuiOutlinedInput-root": {
            borderRadius: 10,
            fontFamily: '"Inter", "Poppins", sans-serif',
            "& fieldset": {
                borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.12)" : "#E2E8F0",
            },
            "&:hover fieldset": {
                borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.2)" : "#CBD5E1",
            },
            "&.Mui-focused fieldset": {
                borderColor: "#0B3C84",
            },
        },
        "& .MuiInputLabel-outlined": {
            fontFamily: '"Inter", "Poppins", sans-serif',
            "&.Mui-focused": {
                color: "#0B3C84",
            },
        },
        "& .MuiSelect-select": {
            fontFamily: '"Inter", "Poppins", sans-serif',
        },
    },
    saveButton: {
        fontFamily: '"Inter", "Poppins", sans-serif',
        fontWeight: 600,
        fontSize: 14,
        textTransform: "none",
        borderRadius: 10,
        padding: "8px 24px",
        backgroundColor: "#0B3C84",
        color: "#FFFFFF",
        boxShadow: "none",
        "&:hover": {
            backgroundColor: "#082D63",
            boxShadow: "0 2px 8px rgba(11,60,132,0.3)",
        },
    },
    menuItem: {
        fontFamily: '"Inter", "Poppins", sans-serif',
    },
    textRight: {
        textAlign: 'right'
    },
    row: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2)
    },
    control: {
        paddingRight: theme.spacing(1),
        paddingLeft: theme.spacing(1)
    },
    buttonContainer: {
        textAlign: 'right',
        padding: theme.spacing(2)
    }
}));

export function PlanManagerForm(props) {
    const { onSubmit, onDelete, onCancel, initialValue, loading } = props;
    const classes = useStyles()

    const [record, setRecord] = useState({
        name: '',
        users: 0,
        connections: 0,
        queues: 0,
        amount: 0,
        useWhatsapp: true,
        useFacebook: true,
        useInstagram: true,
        useCampaigns: true,
        useSchedules: true,
        useInternalChat: true,
        useExternalApi: true,
        useKanban: true,
        useOpenAi: true,
        useIntegration: true,
        isPublic: true,
    });

    useEffect(() => {
        setRecord(initialValue)
    }, [initialValue])

    const handleSubmit = async (data) => {
        onSubmit(data)
    }

    return (
        <Formik
            enableReinitialize
            className={classes.fullWidth}
            initialValues={record}
            onSubmit={(values, { resetForm }) =>
                setTimeout(() => {
                    handleSubmit(values)
                    resetForm()
                }, 500)
            }
        >
            {(values) => (
                <Form className={classes.fullWidth}>
                    <Grid spacing={2} justifyContent="flex-end" container>
                        
                        {/* NOME */}
                        <Grid xs={12} sm={6} md={2} item>
                            <Field
                                as={TextField}
                                label={i18n.t("plans.form.name")}
                                name="name"
                                variant="outlined"
                                className={classes.textfield}
                                margin="dense"
                            />
                        </Grid>

                        {/* USUARIOS */}
                        <Grid xs={12} sm={6} md={1} item>
                            <Field
                                as={TextField}
                                label={i18n.t("plans.form.users")}
                                name="users"
                                variant="outlined"
                                className={classes.textfield}
                                margin="dense"
                                type="number"
                            />
                        </Grid>

                        {/* CONEXOES */}
                        <Grid xs={12} sm={6} md={1} item>
                            <Field
                                as={TextField}
                                label={i18n.t("plans.form.connections")}
                                name="connections"
                                variant="outlined"
                                className={classes.textfield}
                                margin="dense"
                                type="number"
                            />
                        </Grid>
                        
                        {/* FILAS */}
                        <Grid xs={12} sm={6} md={1} item>
                            <Field
                                as={TextField}
                                label="Filas"
                                name="queues"
                                variant="outlined"
                                className={classes.textfield}
                                margin="dense"
                                type="number"
                            />
                        </Grid>
                        
                        {/* VALOR */}
                        <Grid xs={12} sm={6} md={1} item>
                            <Field
                                as={TextField}
                                label="Valor"
                                name="amount"
                                variant="outlined"
                                className={classes.textfield}
                                margin="dense"
                                type="text"
                            />
                        </Grid>
                        
                        {/* WHATSAPP */}
                        <Grid xs={12} sm={6} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth className={classes.selectContainer}>
                                <InputLabel htmlFor="useWhatsapp-selection">Whatsapp</InputLabel>
                                <Field
                                    as={Select}
                                    id="useWhatsapp-selection"
                                    label="Whatsapp"
                                    labelId="useWhatsapp-selection-label"
                                    name="useWhatsapp"
                                    margin="dense"
                                >
                                    <MenuItem className={classes.menuItem} value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem className={classes.menuItem} value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* FACEBOOK */}
                        <Grid xs={12} sm={6} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth className={classes.selectContainer}>
                                <InputLabel htmlFor="useFacebook-selection">Facebook</InputLabel>
                                <Field
                                    as={Select}
                                    id="useFacebook-selection"
                                    label="Facebook"
                                    labelId="useFacebook-selection-label"
                                    name="useFacebook"
                                    margin="dense"
                                >
                                    <MenuItem className={classes.menuItem} value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem className={classes.menuItem} value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* INSTAGRAM */}
                        <Grid xs={12} sm={6} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth className={classes.selectContainer}>
                                <InputLabel htmlFor="useInstagram-selection">Instagram</InputLabel>
                                <Field
                                    as={Select}
                                    id="useInstagram-selection"
                                    label="Instagram"
                                    labelId="useInstagram-selection-label"
                                    name="useInstagram"
                                    margin="dense"
                                >
                                    <MenuItem className={classes.menuItem} value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem className={classes.menuItem} value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* CAMPANHAS */}
                        <Grid xs={12} sm={6} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth className={classes.selectContainer}>
                                <InputLabel htmlFor="useCampaigns-selection">{i18n.t("plans.form.campaigns")}</InputLabel>
                                <Field
                                    as={Select}
                                    id="useCampaigns-selection"
                                    label={i18n.t("plans.form.campaigns")}
                                    labelId="useCampaigns-selection-label"
                                    name="useCampaigns"
                                    margin="dense"
                                >
                                    <MenuItem className={classes.menuItem} value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem className={classes.menuItem} value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* AGENDAMENTOS */}
                        <Grid xs={12} sm={8} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth className={classes.selectContainer}>
                                <InputLabel htmlFor="useSchedules-selection">{i18n.t("plans.form.schedules")}</InputLabel>
                                <Field
                                    as={Select}
                                    id="useSchedules-selection"
                                    label={i18n.t("plans.form.schedules")}
                                    labelId="useSchedules-selection-label"
                                    name="useSchedules"
                                    margin="dense"
                                >
                                    <MenuItem className={classes.menuItem} value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem className={classes.menuItem} value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* CHAT INTERNO */}
                        <Grid xs={12} sm={8} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth className={classes.selectContainer}>
                                <InputLabel htmlFor="useInternalChat-selection">Chat Interno</InputLabel>
                                <Field
                                    as={Select}
                                    id="useInternalChat-selection"
                                    label="Chat Interno"
                                    labelId="useInternalChat-selection-label"
                                    name="useInternalChat"
                                    margin="dense"
                                >
                                    <MenuItem className={classes.menuItem} value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem className={classes.menuItem} value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* API Externa */}
                        <Grid xs={12} sm={8} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth className={classes.selectContainer}>
                                <InputLabel htmlFor="useExternalApi-selection">API Externa</InputLabel>
                                <Field
                                    as={Select}
                                    id="useExternalApi-selection"
                                    label="API Externa"
                                    labelId="useExternalApi-selection-label"
                                    name="useExternalApi"
                                    margin="dense"
                                >
                                    <MenuItem className={classes.menuItem} value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem className={classes.menuItem} value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                         {/* KANBAN */}
                         <Grid xs={12} sm={8} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth className={classes.selectContainer}>
                                <InputLabel htmlFor="useKanban-selection">Kanban</InputLabel>
                                <Field
                                    as={Select}
                                    id="useKanban-selection"
                                    label="Kanban"
                                    labelId="useKanban-selection-label"
                                    name="useKanban"
                                    margin="dense"
                                >
                                    <MenuItem className={classes.menuItem} value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem className={classes.menuItem} value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>
                        {/* OpenAI */}
                        <Grid xs={12} sm={8} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth className={classes.selectContainer}>
                                <InputLabel htmlFor="useOpenAi-selection">OpenAI</InputLabel>
                                <Field
                                    as={Select}
                                    id="useOpenAi-selection"
                                    label="OpenAI"
                                    labelId="useOpenAi-selection-label"
                                    name="useOpenAi"
                                    margin="dense"
                                >
                                    <MenuItem className={classes.menuItem} value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem className={classes.menuItem} value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>
                        {/* Integrações */ }
                        <Grid xs={12} sm={8} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth className={classes.selectContainer}>
                                <InputLabel htmlFor="useIntegration-selection">Integrações</InputLabel>
                                <Field
                                    as={Select}
                                    id="useIntegration-selection"
                                    label="Integrações"
                                    labelId="useIntegration-selection-label"
                                    name="useIntegration"
                                    margin="dense"
                                >
                                    <MenuItem className={classes.menuItem} value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem className={classes.menuItem} value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>

                        {/* Plano Público */ }
                        <Grid xs={12} sm={8} md={2} item>
                            <FormControl margin="dense" variant="outlined" fullWidth className={classes.selectContainer}>
                                <InputLabel htmlFor="isPublic-selection">Público</InputLabel>
                                <Field
                                    as={Select}
                                    id="isPublic-selection"
                                    label="isPublic"
                                    labelId="isPublic-selection-label"
                                    name="isPublic"
                                    margin="dense"
                                >
                                    <MenuItem className={classes.menuItem} value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                                    <MenuItem className={classes.menuItem} value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                                </Field>
                            </FormControl>
                        </Grid>
                        
                        <Grid sm={3} md={1} item>
                            <ButtonWithSpinner className={classes.saveButton} loading={loading} onClick={() => onCancel()} variant="outlined">
                            {i18n.t("plans.form.clear")}
                            </ButtonWithSpinner>
                        </Grid>
                        {record.id !== undefined ? (
                            <Grid sm={3} md={1} item>
                                <ButtonWithSpinner className={classes.saveButton} loading={loading} onClick={() => onDelete(record)} variant="outlined" style={{ color: "#EF4444", borderColor: "#EF4444" }}>
                                {i18n.t("plans.form.delete")}
                                </ButtonWithSpinner>
                            </Grid>
                        ) : null}
                        <Grid sm={3} md={1} item>
                            <ButtonWithSpinner className={classes.saveButton} loading={loading} type="submit" variant="contained" color="primary">
                            {i18n.t("plans.form.save")}
                            </ButtonWithSpinner>
                        </Grid>
                    </Grid>
                </Form>
            )}
        </Formik>
    )
}

export function PlansManagerGrid(props) {
    const { records, onSelect } = props
    const classes = useStyles()
    const tableClasses = useTableStyles()

    const renderWhatsapp = (row) => {
        return row.useWhatsapp === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderFacebook = (row) => {
        return row.useFacebook === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderInstagram = (row) => {
        return row.useInstagram === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderCampaigns = (row) => {
        return row.useCampaigns === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderSchedules = (row) => {
        return row.useSchedules === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderInternalChat = (row) => {
        return row.useInternalChat === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderExternalApi = (row) => {
        return row.useExternalApi === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderKanban = (row) => {
        return row.useKanban === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderOpenAi = (row) => {
        return row.useOpenAi === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderIntegration = (row) => {
        return row.useIntegration === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };

    const renderIsPublic = (row) => {
        return row.isPublic === false ? `${i18n.t("plans.form.no")}` : `${i18n.t("plans.form.yes")}`;
    };
    return (
        <Paper className={tableClasses.tableContainer} elevation={0}>
            <Table
                className={classes.fullWidth}
                padding="normal"
                aria-label="plans table"
            >
                <TableHead className={tableClasses.tableHead}>
                    <TableRow>
                        <TableCell className={tableClasses.tableHeadCell} align="center" style={{ width: '1%' }}>#</TableCell>
                        <TableCell className={tableClasses.tableHeadCell} align="left">{i18n.t("plans.form.name")}</TableCell>
                        <TableCell className={tableClasses.tableHeadCell} align="center">{i18n.t("plans.form.users")}</TableCell>
                        <TableCell className={tableClasses.tableHeadCell} align="center">{i18n.t("plans.form.connections")}</TableCell>
                        <TableCell className={tableClasses.tableHeadCell} align="center">Filas</TableCell>
                        <TableCell className={tableClasses.tableHeadCell} align="center">Valor</TableCell>
                        <TableCell className={tableClasses.tableHeadCell} align="center">Whatsapp</TableCell>
                        <TableCell className={tableClasses.tableHeadCell} align="center">Facebook</TableCell>
                        <TableCell className={tableClasses.tableHeadCell} align="center">Instagram</TableCell>
                        <TableCell className={tableClasses.tableHeadCell} align="center">{i18n.t("plans.form.campaigns")}</TableCell>
                        <TableCell className={tableClasses.tableHeadCell} align="center">{i18n.t("plans.form.schedules")}</TableCell>
                        <TableCell className={tableClasses.tableHeadCell} align="center">Chat Interno</TableCell>
                        <TableCell className={tableClasses.tableHeadCell} align="center">API Externa</TableCell>
                        <TableCell className={tableClasses.tableHeadCell} align="center">Kanban</TableCell>
                        <TableCell className={tableClasses.tableHeadCell} align="center">OpenAI</TableCell>
                        <TableCell className={tableClasses.tableHeadCell} align="center">Integrações</TableCell>
                        <TableCell className={tableClasses.tableHeadCell} align="center">Público</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {records.map((row) => (
                        <TableRow className={tableClasses.tableRow} key={row.id}>
                            <TableCell className={tableClasses.tableCell} align="center" style={{ width: '1%' }}>
                                <IconButton 
                                    onClick={() => onSelect(row)} 
                                    aria-label="edit"
                                    className={tableClasses.actionButton}
                                    size="small"
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </TableCell>
                            <TableCell className={tableClasses.tableCell} align="left">{row.name || '-'}</TableCell>
                            <TableCell className={tableClasses.tableCell} align="center">{row.users || '-'}</TableCell>
                            <TableCell className={tableClasses.tableCell} align="center">{row.connections || '-'}</TableCell>
                            <TableCell className={tableClasses.tableCell} align="center">{row.queues || '-'}</TableCell>
                            <TableCell className={tableClasses.tableCell} align="center">{i18n.t("plans.form.money")} {row.amount ? row.amount.toLocaleString('pt-br', { minimumFractionDigits: 2 }) : '00.00'}</TableCell>
                            <TableCell className={tableClasses.tableCell} align="center">{renderWhatsapp(row)}</TableCell>
                            <TableCell className={tableClasses.tableCell} align="center">{renderFacebook(row)}</TableCell>
                            <TableCell className={tableClasses.tableCell} align="center">{renderInstagram(row)}</TableCell>
                            <TableCell className={tableClasses.tableCell} align="center">{renderCampaigns(row)}</TableCell>
                            <TableCell className={tableClasses.tableCell} align="center">{renderSchedules(row)}</TableCell>
                            <TableCell className={tableClasses.tableCell} align="center">{renderInternalChat(row)}</TableCell>
                            <TableCell className={tableClasses.tableCell} align="center">{renderExternalApi(row)}</TableCell>
                            <TableCell className={tableClasses.tableCell} align="center">{renderKanban(row)}</TableCell>
                            <TableCell className={tableClasses.tableCell} align="center">{renderOpenAi(row)}</TableCell>
                            <TableCell className={tableClasses.tableCell} align="center">{renderIntegration(row)}</TableCell>
                            <TableCell className={tableClasses.tableCell} align="center">{renderIsPublic(row)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Paper>
    )
}

export default function PlansManager() {
    const classes = useStyles()
    const { list, save, update, remove } = usePlans()

    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [loading, setLoading] = useState(false)
    const [records, setRecords] = useState([])
    const [record, setRecord] = useState({
        name: '',
        users: 0,
        connections: 0,
        queues: 0,
        amount: 0,
        useWhatsapp: true,
        useFacebook: true,
        useInstagram: true,
        useCampaigns: true,
        useSchedules: true,
        useInternalChat: true,
        useExternalApi: true,
        useKanban: true,
        useOpenAi: true,
        useIntegration: true,
        isPublic: true,
    })

    useEffect(() => {
        async function fetchData() {
            await loadPlans()
        }
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const loadPlans = async () => {
        setLoading(true)
        try {
            const planList = await list()
            setRecords(planList)
        } catch (e) {
            toast.error('Não foi possível carregar a lista de registros')
        }
        setLoading(false)
    }

    const handleSubmit = async (data) => {
        setLoading(true)
        try {
            if (data.id !== undefined) {
                await update(data)
            } else {
                await save(data)
            }
            await loadPlans()
            handleCancel()
            toast.success('Operação realizada com sucesso!')
        } catch (e) {
            toast.error('Não foi possível realizar a operação. Verifique se já existe uma plano com o mesmo nome ou se os campos foram preenchidos corretamente')
        }
        setLoading(false)
    }

    const handleDelete = async () => {
        setLoading(true)
        try {
            await remove(record.id)
            await loadPlans()
            handleCancel()
            toast.success('Operação realizada com sucesso!')
        } catch (e) {
            toast.error('Não foi possível realizar a operação')
        }
        setLoading(false)
    }

    const handleOpenDeleteDialog = () => {
        setShowConfirmDialog(true)
    }

    const handleCancel = () => {
        setRecord({
            name: '',
            users: 0,
            connections: 0,
            queues: 0,
            amount: 0,
            useWhatsapp: true,
            useFacebook: true,
            useInstagram: true,
            useCampaigns: true,
            useSchedules: true,
            useInternalChat: true,
            useExternalApi: true,
            isPublic: true,
        })
    }

    const handleSelect = (data) => {

        let useWhatsapp = data.useWhatsapp === false ? false : true
        let useFacebook = data.useFacebook === false ? false : true
        let useInstagram = data.useInstagram === false ? false : true
        let useCampaigns = data.useCampaigns === false ? false : true
        let useSchedules = data.useSchedules === false ? false : true
        let useInternalChat = data.useInternalChat === false ? false : true
        let useExternalApi = data.useExternalApi === false ? false : true
        let useKanban = data.useKanban === false ? false : true
        let useOpenAi = data.useOpenAi === false ? false : true
        let useIntegration = data.useIntegration === false ? false : true
        let isPublic = data.isPublic === false ? false : true

        setRecord({
            id: data.id,
            name: data.name || '',
            users: data.users || 0,
            connections: data.connections || 0,
            queues: data.queues || 0,
            amount: data.amount?.toLocaleString('pt-br', { minimumFractionDigits: 2 }) || 0,
            useWhatsapp,
            useFacebook,
            useInstagram,
            useCampaigns,
            useSchedules,
            useInternalChat,
            useExternalApi,
            useKanban,
            useOpenAi,
            useIntegration,
            isPublic
        })
    }

    return (
        <Paper className={classes.mainPaper} elevation={0}>
            <Grid spacing={2} container>
                <Grid xs={12} item>
                    <PlanManagerForm
                        initialValue={record}
                        onDelete={handleOpenDeleteDialog}
                        onSubmit={handleSubmit}
                        onCancel={handleCancel}
                        loading={loading}
                    />
                </Grid>
                <Grid xs={12} item>
                    <PlansManagerGrid
                        records={records}
                        onSelect={handleSelect}
                    />
                </Grid>
            </Grid>
            <ConfirmationModal
                title="Exclusão de Registro"
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                onConfirm={() => handleDelete()}
            >
                Deseja realmente excluir esse registro?
            </ConfirmationModal>
        </Paper>
    )
}